import {
    Close,
    ExpandMore,
    KeyboardDoubleArrowLeft,
    KeyboardDoubleArrowRight,
    SearchOutlined,
} from "@mui/icons-material";
import { Badge, Box, Collapse, Drawer, Fade, IconButton, InputBase, Paper, Popper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMenuLateralContext } from "../../contexts/MenuLateralContext";
import { useNotificacoesContext } from "../../contexts/NotificacoesContext";
import { useThemeContext } from "../../contexts/ThemeContext";
import { useNavegacao } from "../../hooks/useNavegacao";
import { useTamanhoTela } from "../../hooks/useTamanhoTela";
import { IItemNavegacao, IModuloNavegacao } from "../../interfaces/INavegacao";
import {
    AreaRolagem,
    BotaoItem,
    BotaoModulo,
    CaixaBusca,
    Contador,
    ListaItens,
    Rodape,
    RotuloGrupo,
} from "./styles";

const ALTURA_HEADER = 50;
const LARGURA_MOBILE = 288;
const MAX_RESULTADOS_BUSCA = 12;
const MAX_CONTADOR = 99;

const rotularContador = (total: number) => (total > MAX_CONTADOR ? `${MAX_CONTADOR}+` : `${total}`);

/**
 * Menu único do sistema: lista todos os módulos e suas telas num só lugar,
 * agrupados por área. Pode ser recolhido para um rail de ícones, onde cada
 * módulo abre seus itens num painel flutuante.
 */
export const MenuLateral = () => {
    const navigate = useNavigate();
    const { modeloTela } = useTamanhoTela();
    const { logo } = useThemeContext();
    const { recolhido, alternarRecolhido, largura, mobileAberto, fecharMenuMobile } = useMenuLateralContext();
    const { grupos, moduloAtivo, urlAtiva, caminhoAtivo, itensPlanos } = useNavegacao();
    const { contadores } = useNotificacoesContext();

    const mobile = modeloTela === "mobile";
    const compacto = recolhido && !mobile;

    const [moduloAberto, setModuloAberto] = useState<string | undefined>(moduloAtivo?.id);
    const [itensAbertos, setItensAbertos] = useState<string[]>(caminhoAtivo);
    const [busca, setBusca] = useState("");
    const [flyout, setFlyout] = useState<{ modulo: IModuloNavegacao; ancora: HTMLElement } | null>(null);
    const temporizadorFlyout = useRef<ReturnType<typeof setTimeout>>();

    const moduloAtivoId = moduloAtivo?.id;
    const chaveCaminhoAtivo = caminhoAtivo.join("|");

    // Acordeão: o módulo da rota atual abre sozinho.
    useEffect(() => {
        if (moduloAtivoId) setModuloAberto(moduloAtivoId);
    }, [moduloAtivoId]);

    // Mantém abertos os agrupamentos que contêm a tela atual.
    useEffect(() => {
        if (!chaveCaminhoAtivo) return;
        setItensAbertos(atual => Array.from(new Set([...atual, ...chaveCaminhoAtivo.split("|")])));
    }, [chaveCaminhoAtivo]);

    useEffect(() => () => clearTimeout(temporizadorFlyout.current), []);

    // Recolher o menu fecha qualquer painel flutuante pendente.
    useEffect(() => {
        if (!compacto) setFlyout(null);
    }, [compacto]);

    const resultadosBusca = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return [];

        return itensPlanos
            .filter(item =>
                `${item.modulo.titulo} ${item.caminho.join(" ")} ${item.titulo}`.toLowerCase().includes(termo)
            )
            .slice(0, MAX_RESULTADOS_BUSCA);
    }, [busca, itensPlanos]);

    const abrirFlyout = (modulo: IModuloNavegacao, ancora: HTMLElement) => {
        clearTimeout(temporizadorFlyout.current);
        setFlyout({ modulo, ancora });
    };

    const agendarFecharFlyout = () => {
        clearTimeout(temporizadorFlyout.current);
        temporizadorFlyout.current = setTimeout(() => setFlyout(null), 180);
    };

    const navegar = (url: string) => {
        clearTimeout(temporizadorFlyout.current);
        setFlyout(null);
        setBusca("");
        if (mobile) fecharMenuMobile();
        navigate(url);
    };

    const acionarModulo = (modulo: IModuloNavegacao) => {
        if (compacto || !modulo.itens?.length) {
            navegar(modulo.url);
            return;
        }

        setModuloAberto(atual => (atual === modulo.id ? undefined : modulo.id));
    };

    const alternarItem = (url: string) =>
        setItensAbertos(atual => (atual.includes(url) ? atual.filter(item => item !== url) : [...atual, url]));

    const contadorDoItem = (item: IItemNavegacao): number =>
        (item.notificacao ? contadores[item.notificacao] ?? 0 : 0) +
        (item.subitens ?? []).reduce((total, subitem) => total + contadorDoItem(subitem), 0);

    // O módulo mostra a soma do que está dentro dele — assim a pendência
    // aparece mesmo com o acordeão fechado ou com o menu recolhido.
    const contadorDoModulo = (modulo: IModuloNavegacao): number =>
        (modulo.notificacao ? contadores[modulo.notificacao] ?? 0 : 0) +
        (modulo.itens ?? []).reduce((total, item) => total + contadorDoItem(item), 0);

    const renderizarItens = (itens: IItemNavegacao[], nivel = 0, comGuia = true) => {
        const Envolvente = comGuia ? ListaItens : Box;

        return (
            <Envolvente
                sx={
                    comGuia
                        ? nivel > 0
                            ? { ml: 1.5 }
                            : undefined
                        : { display: "flex", flexDirection: "column", gap: "2px" }
                }
            >
                {itens.map(item => {
                    const temSubitens = !!item.subitens?.length;
                    const aberto = itensAbertos.includes(item.url);
                    const ativo = urlAtiva === item.url;
                    const contador = contadorDoItem(item);

                    return (
                        <Box key={item.url}>
                            <BotaoItem
                                ativo={ativo}
                                comGuia={comGuia}
                                onClick={() => (temSubitens ? alternarItem(item.url) : navegar(item.url))}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        flex: 1,
                                        fontWeight: ativo ? 600 : 500,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {item.titulo}
                                </Box>
                                {contador > 0 && (
                                    <Contador aria-label={`${contador} não lidas`}>
                                        {rotularContador(contador)}
                                    </Contador>
                                )}
                                {temSubitens && (
                                    <ExpandMore
                                        sx={{
                                            fontSize: 16,
                                            opacity: 0.6,
                                            transition: "transform .2s",
                                            transform: aberto ? "rotate(180deg)" : "none",
                                        }}
                                    />
                                )}
                            </BotaoItem>
                            {temSubitens && (
                                <Collapse in={aberto} timeout="auto" unmountOnExit>
                                    {renderizarItens(item.subitens ?? [], nivel + 1)}
                                </Collapse>
                            )}
                        </Box>
                    );
                })}
            </Envolvente>
        );
    };

    const renderizarModulo = (modulo: IModuloNavegacao) => {
        const Icone = modulo.icone;
        const ativo = moduloAtivo?.id === modulo.id;
        const temItens = !!modulo.itens?.length;
        const aberto = !compacto && temItens && moduloAberto === modulo.id;
        const contador = contadorDoModulo(modulo);

        return (
            <Box
                key={modulo.id}
                sx={{ mb: "2px" }}
                onMouseEnter={compacto ? evento => abrirFlyout(modulo, evento.currentTarget) : undefined}
                onMouseLeave={compacto ? agendarFecharFlyout : undefined}
            >
                <BotaoModulo ativo={ativo} compacto={compacto} onClick={() => acionarModulo(modulo)}>
                    {compacto && contador > 0 ? (
                        <Badge
                            badgeContent={rotularContador(contador)}
                            color="error"
                            overlap="circular"
                            sx={{ "& .MuiBadge-badge": { fontSize: "0.625rem", height: 16, minWidth: 16, px: 0.5 } }}
                        >
                            <Icone sx={{ fontSize: 21 }} />
                        </Badge>
                    ) : (
                        <Icone sx={{ fontSize: 21 }} />
                    )}
                    {!compacto && (
                        <>
                            <Box
                                component="span"
                                sx={{
                                    flex: 1,
                                    fontSize: "0.8125rem",
                                    fontWeight: ativo ? 600 : 500,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {modulo.titulo}
                            </Box>
                            {contador > 0 && !aberto && (
                                <Contador aria-label={`${contador} não lidas`}>
                                    {rotularContador(contador)}
                                </Contador>
                            )}
                            {temItens && (
                                <ExpandMore
                                    sx={{
                                        fontSize: 18,
                                        opacity: 0.6,
                                        transition: "transform .2s",
                                        transform: aberto ? "rotate(180deg)" : "none",
                                    }}
                                />
                            )}
                        </>
                    )}
                </BotaoModulo>
                {!compacto && temItens && (
                    <Collapse in={aberto} timeout="auto" unmountOnExit>
                        {renderizarItens(modulo.itens ?? [])}
                    </Collapse>
                )}
            </Box>
        );
    };

    const renderizarResultadosBusca = () => {
        if (!resultadosBusca.length) {
            return (
                <Typography
                    sx={{ px: 1.5, py: 4, fontSize: "0.8125rem", color: "text.secondary", textAlign: "center" }}
                >
                    Nada encontrado para “{busca.trim()}”
                </Typography>
            );
        }

        return resultadosBusca.map(resultado => {
            const Icone = resultado.modulo.icone;
            const trilha = [resultado.modulo.titulo, ...resultado.caminho].join(" › ");
            const contador = resultado.notificacao ? contadores[resultado.notificacao] ?? 0 : 0;

            return (
                <BotaoItem
                    key={`${resultado.modulo.id}-${resultado.url}`}
                    ativo={urlAtiva === resultado.url}
                    onClick={() => navegar(resultado.url)}
                    sx={{ height: 46, mb: "2px", gap: 1.5, px: 1.5 }}
                >
                    <Icone sx={{ fontSize: 19, opacity: 0.75 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.primary" }}>
                            {resultado.titulo}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>
                            {trilha}
                        </Typography>
                    </Box>
                    {contador > 0 && (
                        <Contador aria-label={`${contador} não lidas`}>{rotularContador(contador)}</Contador>
                    )}
                </BotaoItem>
            );
        });
    };

    const conteudo = (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "background.paper" }}>
            {mobile && (
                <Box
                    sx={{
                        height: 56,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: theme => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <img src={logo} alt="Space Food" style={{ height: 30, objectFit: "contain" }} />
                    <IconButton size="small" onClick={fecharMenuMobile}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <Box sx={{ p: 1.25, pb: 0.5 }}>
                {compacto ? (
                    <BotaoModulo compacto onClick={alternarRecolhido} sx={{ height: 38 }}>
                        <SearchOutlined sx={{ fontSize: 19, opacity: 0.75 }} />
                    </BotaoModulo>
                ) : (
                    <CaixaBusca>
                        <SearchOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                        <InputBase
                            value={busca}
                            onChange={evento => setBusca(evento.target.value)}
                            placeholder="Buscar no menu"
                            sx={{ flex: 1, fontSize: "0.8125rem" }}
                        />
                        {!!busca && (
                            <IconButton size="small" onClick={() => setBusca("")}>
                                <Close sx={{ fontSize: 14 }} />
                            </IconButton>
                        )}
                    </CaixaBusca>
                )}
            </Box>

            <AreaRolagem>
                {busca.trim()
                    ? renderizarResultadosBusca()
                    : grupos.map((grupo, indice) => (
                        <Box key={grupo.id}>
                            {compacto ? (
                                indice > 0 && (
                                    <Box
                                        sx={{
                                            my: 1,
                                            mx: "auto",
                                            width: 24,
                                            borderTop: theme => `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                )
                            ) : (
                                <RotuloGrupo sx={indice === 0 ? { mt: 1 } : undefined}>{grupo.titulo}</RotuloGrupo>
                            )}
                            {grupo.modulos.map(renderizarModulo)}
                        </Box>
                    ))}
            </AreaRolagem>

            {!mobile && (
                <Rodape>
                    <BotaoModulo compacto={compacto} onClick={alternarRecolhido} sx={{ height: 38 }}>
                        {compacto ? (
                            <KeyboardDoubleArrowRight sx={{ fontSize: 20, opacity: 0.75 }} />
                        ) : (
                            <>
                                <KeyboardDoubleArrowLeft sx={{ fontSize: 20, opacity: 0.75 }} />
                                <Box
                                    component="span"
                                    sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary" }}
                                >
                                    Recolher menu
                                </Box>
                            </>
                        )}
                    </BotaoModulo>
                </Rodape>
            )}
        </Box>
    );

    if (mobile) {
        return (
            <Drawer
                variant="temporary"
                anchor="left"
                open={mobileAberto}
                onClose={fecharMenuMobile}
                ModalProps={{ keepMounted: true }}
                sx={{
                    zIndex: 99999,
                    "& .MuiDrawer-paper": {
                        width: LARGURA_MOBILE,
                        boxSizing: "border-box",
                        backgroundImage: "none",
                    },
                }}
            >
                {conteudo}
            </Drawer>
        );
    }

    return (
        <>
            <Drawer
                variant="permanent"
                anchor="left"
                sx={{
                    width: largura,
                    flexShrink: 0,
                    transition: theme => theme.transitions.create("width", { duration: 220 }),
                    "& .MuiDrawer-paper": {
                        width: largura,
                        boxSizing: "border-box",
                        overflow: "hidden",
                        marginTop: `${ALTURA_HEADER}px`,
                        height: `calc(100% - ${ALTURA_HEADER}px)`,
                        borderRight: theme => `1px solid ${theme.palette.divider}`,
                        backgroundImage: "none",
                        transition: theme => theme.transitions.create("width", { duration: 220 }),
                    },
                }}
            >
                {conteudo}
            </Drawer>

            <Popper
                open={compacto && !!flyout}
                anchorEl={flyout?.ancora}
                placement="right-start"
                transition
                modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
                sx={{ zIndex: 9998 }}
                onMouseEnter={() => clearTimeout(temporizadorFlyout.current)}
                onMouseLeave={agendarFecharFlyout}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={140}>
                        <Paper
                            elevation={8}
                            sx={{
                                minWidth: 216,
                                p: 1,
                                borderRadius: "12px",
                                backgroundImage: "none",
                                border: theme => `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    px: 1.25,
                                    py: 0.75,
                                    fontSize: "0.6875rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "text.secondary",
                                }}
                            >
                                {flyout?.modulo.titulo}
                            </Typography>
                            {!!flyout?.modulo.itens?.length && renderizarItens(flyout.modulo.itens, 0, false)}
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </>
    );
};
